import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useStore } from "../store";
import "./Settings.css";

const CHANGE_PASSWORD = gql`
  mutation($currentPassword: String!, $newPassword: String!) {
    changePassword(currentPassword: $currentPassword, newPassword: $newPassword)
  }
`;

const GET_NOTIF = gql`
  query { notificationConfig { id siteId enabled telegramBotToken telegramChatId } }
`;
const SET_NOTIF = gql`
  mutation($enabled: Boolean, $telegramBotToken: String, $telegramChatId: String) {
    setNotificationConfig(enabled: $enabled, telegramBotToken: $telegramBotToken, telegramChatId: $telegramChatId) {
      id enabled telegramBotToken telegramChatId
    }
  }
`;
const TEST_NOTIF = gql`mutation { testNotification }`;

const GET_SIM = gql`query { simulatorState { siteId enabled count } }`;
const SET_SIM = gql`mutation($enabled: Boolean, $count: Int) { setSimulator(enabled: $enabled, count: $count) { enabled count } }`;

export default function Settings() {
  const { t } = useTranslation();
  const { soundEnabled, setSoundEnabled, mapCenter, setMapCenter } = useStore();
  const [lat, setLat] = useState(String(mapCenter[0]));
  const [lng, setLng] = useState(String(mapCenter[1]));

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confPw, setConfPw] = useState("");
  const [pwMsg, setPwMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [changePassword, { loading: pwLoading }] = useMutation(CHANGE_PASSWORD);

  // ── Notifications (per-object, Telegram) ──
  const user = useStore(s => s.user);
  // Сайтовые роли (оператор/наблюдатель). Гейтим по роли, а не по siteId из сессии —
  // чтобы блоки показывались без релогина; реальную привязку к объекту сервер берёт из JWT.
  const hasSite = user?.role === "OPERATOR" || user?.role === "VIEWER";
  const notifQ = useQuery(GET_NOTIF, { skip: !hasSite });
  const [nfEnabled, setNfEnabled] = useState(false);
  const [nfToken, setNfToken] = useState("");
  const [nfChat, setNfChat] = useState("");
  const [nfMsg, setNfMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [setNotif, { loading: nfSaving }] = useMutation(SET_NOTIF);
  const [testNotif, { loading: nfTesting }] = useMutation(TEST_NOTIF);

  useEffect(() => {
    const c = notifQ.data?.notificationConfig;
    if (c) { setNfEnabled(!!c.enabled); setNfToken(c.telegramBotToken || ""); setNfChat(c.telegramChatId || ""); }
  }, [notifQ.data]);

  // ── Simulator (temporary control) ──
  const simQ = useQuery(GET_SIM, { skip: !hasSite });
  const [simEnabled, setSimEnabled] = useState(false);
  const [simCount, setSimCount] = useState("8");
  const [simMsg, setSimMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [setSim, { loading: simSaving }] = useMutation(SET_SIM, { onCompleted: () => simQ.refetch() });
  useEffect(() => {
    const s = simQ.data?.simulatorState;
    if (s) { setSimEnabled(!!s.enabled); setSimCount(String(s.count ?? 8)); }
  }, [simQ.data]);
  const applySim = async () => {
    setSimMsg(null);
    try {
      await setSim({ variables: { enabled: simEnabled, count: parseInt(simCount || "0", 10) || 0 } });
      setSimMsg({ ok: true, text: "Applied." });
    } catch (e: any) { setSimMsg({ ok: false, text: e.message.replace(/^.*Error:\s*/, "") }); }
  };

  const saveNotif = async () => {
    setNfMsg(null);
    try {
      await setNotif({ variables: { enabled: nfEnabled, telegramBotToken: nfToken, telegramChatId: nfChat } });
      setNfMsg({ ok: true, text: "Saved." });
    } catch (e: any) { setNfMsg({ ok: false, text: e.message.replace(/^.*Error:\s*/, "") }); }
  };
  const sendTest = async () => {
    setNfMsg(null);
    try {
      await setNotif({ variables: { enabled: nfEnabled, telegramBotToken: nfToken, telegramChatId: nfChat } });
      await testNotif();
      setNfMsg({ ok: true, text: "Test message sent — check your Telegram." });
    } catch (e: any) { setNfMsg({ ok: false, text: e.message.replace(/^.*Error:\s*/, "") }); }
  };

  const handleSaveCenter = () => {
    const la = parseFloat(lat), ln = parseFloat(lng);
    if (!isNaN(la) && !isNaN(ln)) setMapCenter([la, ln]);
  };

  const handleChangePassword = async () => {
    setPwMsg(null);
    if (newPw.length < 8) { setPwMsg({ ok: false, text: "New password must be at least 8 characters." }); return; }
    if (newPw !== confPw) { setPwMsg({ ok: false, text: "New password and confirmation do not match." }); return; }
    try {
      await changePassword({ variables: { currentPassword: curPw, newPassword: newPw } });
      setPwMsg({ ok: true, text: "Password changed successfully." });
      setCurPw(""); setNewPw(""); setConfPw("");
    } catch (e: any) {
      setPwMsg({ ok: false, text: e.message.replace(/^.*Error:\s*/, "") });
    }
  };

  return (
    <div className="page-container">
      <div className="page-header"><h2>⚙️ {t('settings.title')}</h2></div>
      <div className="settings-sections">

        <div className="settings-section">
          <h3>{t('settings.alertsTelegram')}</h3>
          {!hasSite ? (
            <div className="setting-desc">Sign in as a site operator to configure alerts for an object.</div>
          ) : (
            <>
              <div className="setting-row">
                <div>
                  <div className="setting-label">Notify on DEFENSE-zone breach</div>
                  <div className="setting-desc">Send an instant Telegram message when a target enters a DEFENSE zone of this object.</div>
                </div>
                <label className="toggle">
                  <input type="checkbox" checked={nfEnabled} onChange={e => setNfEnabled(e.target.checked)} />
                  <span className="toggle-slider" />
                </label>
              </div>
              <div className="setting-inputs" style={{ flexDirection: "column", alignItems: "stretch", gap: 8, maxWidth: 480 }}>
                <input className="input-field" placeholder="Telegram bot token" value={nfToken} onChange={e => setNfToken(e.target.value)} />
                <input className="input-field" placeholder="Telegram chat ID" value={nfChat} onChange={e => setNfChat(e.target.value)} />
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn-primary" disabled={nfSaving} onClick={saveNotif}>{nfSaving ? "Saving..." : "Save"}</button>
                  <button className="btn-primary" disabled={nfTesting || !nfToken || !nfChat} onClick={sendTest}>{nfTesting ? "Sending..." : "Send test"}</button>
                </div>
                {nfMsg && <div className="setting-hint" style={{ color: nfMsg.ok ? "#2ecc71" : "#e74c3c" }}>{nfMsg.text}</div>}
                <div className="setting-hint">
                  Setup: 1) In Telegram, message <b>@BotFather</b> → <i>/newbot</i> → copy the bot token.
                  2) Add the bot to your group/channel (or message it directly), then get the chat ID via <b>@userinfobot</b> or @getidsbot.
                  3) Paste both above, enable, and Send test.
                </div>
              </div>
            </>
          )}
        </div>

        {hasSite && (
          <div className="settings-section">
            <h3>{t('settings.simulator')} <span style={{ fontSize: 12, opacity: 0.6, fontWeight: 400 }}>{t('settings.temporary')}</span></h3>
            <div className="setting-row">
              <div>
                <div className="setting-label">Drone simulation</div>
                <div className="setting-desc">Generate simulated targets on this object's map for demos and testing.</div>
              </div>
              <label className="toggle">
                <input type="checkbox" checked={simEnabled} onChange={e => setSimEnabled(e.target.checked)} />
                <span className="toggle-slider" />
              </label>
            </div>
            <div className="setting-inputs" style={{ alignItems: "center", gap: 8 }}>
              <span className="setting-desc" style={{ minWidth: 130 }}>Number of targets</span>
              <input className="input-field" style={{ width: 90 }} type="number" min={0} max={200} value={simCount} onChange={e => setSimCount(e.target.value)} disabled={!simEnabled} />
              <button className="btn-primary" disabled={simSaving} onClick={applySim}>{simSaving ? "Applying..." : "Apply"}</button>
              {simMsg && <span className="setting-hint" style={{ color: simMsg.ok ? "#2ecc71" : "#e74c3c" }}>{simMsg.text}</span>}
            </div>
            <div className="setting-hint">Changes take effect within a few seconds. Temporary control until the full simulator module ships.</div>
          </div>
        )}

        {hasSite && (
          <div className="settings-section">
            <h3>{t('settings.tools')}</h3>
            <div className="setting-row">
              <div>
                <div className="setting-label">Node / Agent</div>
                <div className="setting-desc">Connect this object's detection node or light agent.</div>
              </div>
              <Link className="btn-primary" to="/node">Open</Link>
            </div>
            <div className="setting-row">
              <div>
                <div className="setting-label">Integration — DD-Connect</div>
                <div className="setting-desc">Connect third-party sensors, connectors and the ingest API.</div>
              </div>
              <Link className="btn-primary" to="/integrate">Open</Link>
            </div>
          </div>
        )}

        <div className="settings-section">
          <h3>{t('settings.security')}</h3>
          <div className="setting-row">
            <div>
              <div className="setting-label">Change password</div>
              <div className="setting-desc">Use a strong password (min. 8 characters). Required before going live.</div>
            </div>
          </div>
          <div className="setting-inputs" style={{ flexDirection: "column", alignItems: "stretch", gap: 8, maxWidth: 360 }}>
            <input className="input-field" type="password" placeholder="Current password" value={curPw} onChange={e => setCurPw(e.target.value)} autoComplete="current-password" />
            <input className="input-field" type="password" placeholder="New password" value={newPw} onChange={e => setNewPw(e.target.value)} autoComplete="new-password" />
            <input className="input-field" type="password" placeholder="Confirm new password" value={confPw} onChange={e => setConfPw(e.target.value)} autoComplete="new-password" />
            <button className="btn-primary" disabled={pwLoading || !curPw || !newPw} onClick={handleChangePassword}>
              {pwLoading ? "Saving..." : "Change password"}
            </button>
            {pwMsg && (
              <div className="setting-hint" style={{ color: pwMsg.ok ? "#2ecc71" : "#e74c3c" }}>{pwMsg.text}</div>
            )}
          </div>
        </div>

        <div className="settings-section">
          <h3>{t('settings.soundAlerts')}</h3>
          <div className="setting-row">
            <div>
              <div className="setting-label">Alarm sound</div>
              <div className="setting-desc">Play a sound when a drone is detected inside a zone</div>
            </div>
            <label className="toggle">
              <input type="checkbox" checked={soundEnabled} onChange={e => setSoundEnabled(e.target.checked)} />
              <span className="toggle-slider" />
            </label>
          </div>
        </div>

        <div className="settings-section">
          <h3>{t('settings.mapCenter')}</h3>
          <div className="setting-row">
            <div className="setting-inputs">
              <input className="input-field" placeholder="Latitude" value={lat} onChange={e => setLat(e.target.value)} />
              <input className="input-field" placeholder="Longitude" value={lng} onChange={e => setLng(e.target.value)} />
              <button className="btn-primary" onClick={handleSaveCenter}>Save</button>
            </div>
          </div>
          <div className="setting-hint">Current center: {mapCenter[0].toFixed(4)}, {mapCenter[1].toFixed(4)}</div>
        </div>

        <div className="settings-section">
          <h3>{t('settings.system')}</h3>
          <div className="info-rows">
            <div className="info-row"><span>Version</span><span>3.1</span></div>
            <div className="info-row"><span>Build</span><span>2026.06</span></div>
            <div className="info-row"><span>API</span><span>GraphQL + WebSocket</span></div>
            <div className="info-row"><span>Map</span><span>Leaflet + OpenStreetMap</span></div>
            <div className="info-row"><span>Mode</span><span>DroneDefender Local Edge</span></div>
          </div>
        </div>

      </div>
    </div>
  );
}
